import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Button,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    Alert,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import {
    Person,
    Email,
    Business,
    Home,
    Map,
    Public,
    Numbers,
    Language,
    Edit,
    Save,
    Cancel,
    Shield,
    Phone,
} from "@mui/icons-material";
import { HiChevronRight } from "react-icons/hi";
import apiEndpoints from "../../apiconfig";

const ProfilePage = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedProfile, setEditedProfile] = useState(null);const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);
    const [errors, setErrors] = useState({
    email: "",
    pincode: "",
    gstno: "",
});


    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 425);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // 👉 NOW trim function can access isMobile safely
    const trimIfNeeded = (field, value) => {
        const longFields = ["companyName", "email", "legal_business_address", "company_website"];
        if (!value) return "N/A";

        // Trim ONLY on mobile view
        if (!isMobile) return value;

        if (!longFields.includes(field)) return value;

        return value.length > 10 ? value.slice(0, 10) + "..." : value;
    };

    const getToken = () => {
        return (
            localStorage.getItem("authToken") ||
            localStorage.getItem("token") ||
            sessionStorage.getItem("authToken") ||
            sessionStorage.getItem("token")
        );
    };

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const token = getToken();
                if (!token) {
                    setError("No authentication token found. Please login again.");
                    setLoading(false);
                    return;
                }

                const response = await fetch(apiEndpoints.customer_profile, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ action: "getProfile" }),
                });

                const data = await response.json();

                if (data.status === "success") {
                    setProfile(data.user);
                    setEditedProfile(data.user);
                } else {
                    setError(data.message || "Failed to fetch profile data");
                }
            } catch (err) {
                setError("Error fetching profile data: " + err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, []);

    const handleFieldChange = (field, value) => {
        setEditedProfile((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateFields = () => {
    let tempErrors = { email: "", pincode: "", gstno: "" };
    let isValid = true;

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (editedProfile.email && !emailRegex.test(editedProfile.email)) {
        tempErrors.email = "Invalid email format";
        isValid = false;
    }

    // Pincode max 12 digits
    if (editedProfile.pincode && editedProfile.pincode.length > 12) {
        tempErrors.pincode = "Pincode cannot exceed 12 digits";
        isValid = false;
    }

    // GST No must be exactly 15 characters
    if (editedProfile.gstno && editedProfile.gstno.length !== 15) {
        tempErrors.gstno = "GST number must be exactly 15 characters";
        isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
};


    const handleSaveClick = async () => {
    if (!validateFields()) {
        return; // stop save if validation fails
    }

    try {
        const token = getToken();

        const response = await fetch(apiEndpoints.customer_profile, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                action: "updateProfile",
                ...editedProfile,
            }),
        });

        const data = await response.json();

        if (data.status === "success") {
            setProfile(editedProfile);
            setIsEditing(false);
            setError(null);
        } else {
            setError(data.message || "Failed to update profile");
        }
    } catch (err) {
        setError("Error updating profile: " + err.message);
    }
};


    const handleCancelClick = () => {
        setEditedProfile(profile);
        setIsEditing(false);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "50vh",
                    fontFamily: "Montserrat",
                }}
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading profile...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: "#F9FAFB",
                minHeight: "100vh",
                p: { xs: 2, md: 4 },
                fontFamily: "Montserrat",
            }}
        >
            {/* Header */}
            <Box sx={{ mb: 4, display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="h4" fontWeight={700} color="#1F2937">
                    Profile Overview
                </Typography>

                <Typography variant="body2" color="#6B7280">
                    Manage your account details and business information
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", color: "#D2B887", fontSize: 14, mt: 1 }}>
                    <span>Home</span>
                    <HiChevronRight style={{ margin: "0 6px" }} />
                    <span>Profile</span>
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} mb={4}>
                {[
                    { label: "Account Status", value: profile?.status || "N/A", icon: <Shield /> },
                    { label: "Account ID", value: `CUST-${profile?.id}`, icon: <Numbers /> },
                    { label: "WhatsApp Number", value: profile?.waba_number || "N/A", icon: <Phone /> },
                ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                p: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: "rgba(37,211,102,0.1)",
                                    color: "#25D366",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {item.icon}
                            </Box>

                            <Box>
                                <Typography variant="body2" color="#6B7280">
                                    {item.label}
                                </Typography>
                                <Typography variant="h6" fontWeight={600} color="#1F2937">
                                    {item.value}
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Editable Section */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, p: 4, mb: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Typography variant="h5" fontWeight={700} color="#1F2937">
                        Personal & Business Information
                    </Typography>

                    {!isEditing ? (
                        <Button
                            startIcon={<Edit />}
                            variant="contained"
                            sx={{
                                bgcolor: "#D2B887",
                                color: "#1F2937",
                                fontWeight: 600,
                                "&:hover": { bgcolor: "#C2A56E" },
                            }}
                            onClick={() => setIsEditing(true)}
                        >
                            Edit Profile
                        </Button>
                    ) : (
                        <Box
    sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        width: "100%",
    }}
>

                            <Button
    variant="outlined"
    startIcon={<Cancel />}
    onClick={handleCancelClick}
    sx={{
        borderColor: "#6B7280",
        color: "#6B7280",
        fontWeight: 600,
        width: { xs: "100%", sm: "auto" },  
        "&:hover": { borderColor: "#25D366", color: "#25D366" },
    }}
>
    Cancel
</Button>

<Button
    variant="contained"
    startIcon={<Save />}
    onClick={handleSaveClick}
    sx={{
        bgcolor: "#25D366",
        width: { xs: "100%", sm: "auto" },   
        fontWeight: 600,
        "&:hover": { bgcolor: "#22C35E" },
    }}
>
    Save Changes
</Button>

                        </Box>
                    )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                    {[
                        { label: "Username", field: "primary_contact_name", icon: <Person /> },
                        { label: "Email", field: "email", icon: <Email /> },
                        { label: "Company Name", field: "companyName", icon: <Business /> },
                        { label: "Address", field: "legal_business_address", icon: <Home /> },
                        { label: "City", field: "city", icon: <Map /> },
                        { label: "Country", field: "country", icon: <Public /> },
                        { label: "State", field: "state", icon: <Map /> },
                        { label: "Pincode", field: "pincode", icon: <Numbers /> },
                        { label: "Website", field: "company_website", icon: <Language /> },
                        { label: "GST Number", field: "gstno", icon: <Numbers /> },
                    ].map((item, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    sx={{
                                        p: 1.2,
                                        borderRadius: 2,
                                        bgcolor: "rgba(210,184,135,0.15)",
                                        color: "#D2B887",
                                    }}
                                >
                                    {item.icon}
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="#6B7280" mb={0.5}>
                                        {item.label}
                                    </Typography>

                                    {isEditing ? (
                                        <TextField
    fullWidth
    size="small"
    value={editedProfile?.[item.field] || ""}
    onChange={(e) => handleFieldChange(item.field, e.target.value)}
    error={Boolean(errors[item.field])}
    helperText={errors[item.field] || ""}
/>

                                    ) : (
                                        <Typography
                                            variant="body1"
                                            color="#1F2937"
                                            sx={{
                                                wordBreak: "break-word",
                                                overflowWrap: "break-word",
                                            }}
                                        >
                                            {trimIfNeeded(item.field, editedProfile?.[item.field])}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Card>

            {/* Account Info Table */}
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                        Account Details
                    </Typography>

                    <Paper sx={{ width: "100%", overflowX: "auto" }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#F3F4F6" }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {[
                                    { label: "Account Status", value: profile?.status },
                                    { label: "Created On", value: profile?.createdOn },
                                    { label: "Last Updated", value: profile?.ModifiedOn },
                                    { label: "Support PIN", value: "N/A" },
                                ].map((row, index) => (
                                    <TableRow
                                        key={index}
                                        hover
                                        sx={{
                                            "&:hover": { bgcolor: "rgba(37,211,102,0.05)" },
                                        }}
                                    >
                                        <TableCell>{row.label}</TableCell>
                                        <TableCell>{row.value || "N/A"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ProfilePage;
